import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, Download, FileText, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { getLeadMagnet } from "@/data/leadMagnets";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LeadMagnet() {
  const { slug } = useParams();
  const magnet = getLeadMagnet(slug);

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (magnet) document.title = `${magnet.title} | Track Digital`;
  }, [magnet]);

  if (!magnet) {
    return (
      <main className="min-h-screen bg-white flex items-center justify-center px-6 py-24">
        <div className="text-center max-w-md">
          <h1 className="font-head text-3xl font-semibold mb-4">Resource not found</h1>
          <p className="text-[#6b6b6b] mb-8">The download link you followed doesn't match any current resource.</p>
          <Link to="/" className="td-btn td-btn-primary">Back to home</Link>
        </div>
      </main>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/api/lead-magnet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug: magnet.slug,
          name: firstName.trim(),
          email: email.trim(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Something went wrong. Please try again.");
      }
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <main className="min-h-screen bg-white pt-28 md:pt-36 pb-24" data-testid={`lead-magnet-${magnet.slug}`}>
      <div className="td-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Left column — copy */}
        <div className="lg:col-span-7">
          <Reveal>
            <span className="td-label block mb-6">{magnet.label}</span>
            <h1 className="font-head text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight mb-6">
              {magnet.title}
            </h1>
            <p className="text-lg md:text-xl text-[#3a3a44] leading-relaxed mb-8 max-w-xl">
              {magnet.subtitle}
            </p>
            <p className="text-[#4a4a55] leading-relaxed max-w-xl mb-10">
              {magnet.intro}
            </p>
            <ul className="space-y-4 max-w-lg mb-10">
              {magnet.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] text-[#2b2b2b]">
                  <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: magnet.tint }}>
                    <Check size={14} className="text-[#7C6CF0]" strokeWidth={2.5} />
                  </span>
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* Right column — form / success state */}
        <div className="lg:col-span-5">
          <Reveal delay={120}>
            <div
              className="rounded-3xl p-8 md:p-10 relative overflow-hidden"
              style={{ background: magnet.tint }}
            >
              {/* PDF cover mock */}
              <div className="absolute -top-6 -right-6 opacity-40 pointer-events-none">
                <FileText size={140} strokeWidth={1} className="text-[#7C6CF0]" />
              </div>

              {status !== "success" ? (
                <>
                  <h2 className="font-head text-2xl md:text-3xl font-semibold text-black leading-tight mb-3 relative">
                    Get your free copy
                  </h2>
                  <p className="text-[#3a3a44] mb-7 relative">
                    Pop your details in below and we'll send the PDF straight to your inbox.
                  </p>
                  <form onSubmit={submit} className="space-y-4 relative" data-testid="lead-magnet-form">
                    <div>
                      <label htmlFor="lm-firstName" className="td-label block mb-2">First name</label>
                      <input
                        id="lm-firstName"
                        data-testid="lm-firstname-input"
                        type="text"
                        required
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Your first name"
                        className="w-full rounded-xl bg-white/85 backdrop-blur border border-white px-4 py-3 text-[15px] text-[#1a1a1a] placeholder-[#a0a0a0] focus:outline-none focus:ring-2 focus:ring-[#7C6CF0]"
                      />
                    </div>
                    <div>
                      <label htmlFor="lm-email" className="td-label block mb-2">Email address</label>
                      <input
                        id="lm-email"
                        data-testid="lm-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@business.com"
                        className="w-full rounded-xl bg-white/85 backdrop-blur border border-white px-4 py-3 text-[15px] text-[#1a1a1a] placeholder-[#a0a0a0] focus:outline-none focus:ring-2 focus:ring-[#7C6CF0]"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      data-testid="lm-submit-btn"
                      className="td-btn td-btn-primary w-full mt-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {status === "submitting" ? "Sending…" : "Send it to me"}
                    </button>
                    {status === "error" && (
                      <p className="text-sm text-red-600 mt-3" data-testid="lm-error">
                        {errorMsg}
                      </p>
                    )}
                    <p className="text-xs text-[#6b6b6b] leading-relaxed mt-3">
                      By requesting the checklist, you agree to receive occasional emails from Track Digital.
                      Unsubscribe any time.
                    </p>
                  </form>
                </>
              ) : (
                <div className="relative" data-testid="lm-success">
                  <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center mb-6">
                    <Check size={26} className="text-[#7C6CF0]" strokeWidth={2.5} />
                  </div>
                  <h2 className="font-head text-2xl md:text-3xl font-semibold text-black leading-tight mb-3">
                    Your checklist is ready.
                  </h2>
                  <p className="text-[#3a3a44] mb-6 leading-relaxed">
                    Click below to download your copy. You'll also receive a confirmation email
                    shortly, just click confirm and we'll keep in touch with occasional useful tips.
                  </p>
                  <a
                    href={magnet.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="td-btn td-btn-primary w-full inline-flex items-center justify-center gap-2"
                    data-testid="lm-direct-download"
                  >
                    <Download size={16} /> Download the checklist
                  </a>
                  <Link
                    to="/insights"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#7C6CF0] hover:text-[#5F4FD6] transition-colors"
                  >
                    Read more from Track Digital <ArrowRight size={14} />
                  </Link>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
